import Vue from 'vue'
import { Config } from '@/api/models/config'

const state = {
    config: {},
    initialized: false,
    navScroll: false,
    bodyBackground: null,
    cartOpen: false,
    notifications: false,
    violations: null,
    listView: false,
    highlightedListView: false,
    restaurantView: false,
    brandView: false,
    checkoutView: false,
    listFiltered: false,
}

const mutations = {
    INITIALIZATION_DONE(state) {
        Vue.set(state, 'initialized', true)
    },
    SET_CONFIG(state, val) {
        state.config = val
    },
    SET_SCROLL(state, val) {
        state.navScroll = val
    },
    SET_BODY_BACKGROUND(state, val) {
        state.bodyBackground = val
    },
    SET_CART(state, val) {
        state.cartOpen = val
    },
    SET_VIOLATIONS(state, val) {
        state.violations = val
    },
    RESET_VIOLATIONS(state) {
        state.violations = null
    },
    REMOVE_VIOLATION(state, index) {
        if (state.violations) state.violations.splice(index, 1)
    },
    LIST_VIEW(state, val) {
        Vue.set(state, 'listView', val)
    },
    HIGHLIGHTED_LIST_VIEW(state, val) {
        Vue.set(state, 'highlightedListView', val)
    },
    RESTAURANT_VIEW(state, val) {
        Vue.set(state, 'restaurantView', val)
    },
    BRAND_VIEW(state, val) {
        Vue.set(state, 'brandView', val)
    },
    CHECKOUT_VIEW(state, val) {
        Vue.set(state, 'checkoutView', val)
    },
    LIST_FILTERED(state, val) {
        Vue.set(state, 'listFiltered', val)
    },
}

const actions = {
    async getConfig({ dispatch, commit, state }) {
        try {
            dispatch('loading/start', {}, { root: true })
            let config = new Config()
            const result = await Promise.all([config.fetch(), config.getSettings()])
            await dispatch('setConfig', {
                ...result[1].settings,
                ...result[0].config,
            })
            await dispatch('initLocales', state.config, { root: true })
            dispatch('canteen/setDisabledList', state.config.disable_canteen_banner_for_companies, {
                root: true,
            })
            return state.config
        } catch (e) {
            throw new Error(e)
        } finally {
            commit('INITIALIZATION_DONE')
            dispatch('loading/finish', {}, { root: true })
        }
    },
    setScroll({ commit }, val) {
        commit('SET_SCROLL', val)
    },
    setCart({ commit }, val) {
        commit('SET_CART', val)
    },
    setConfig({ commit }, val) {
        commit('SET_CONFIG', val)
    },
    setViolations({ commit }, val) {
        commit('SET_VIOLATIONS', val)
    },
    resetViolations({ commit }) {
        commit('RESET_VIOLATIONS')
    },
    removeViolation({ commit }, index) {
        commit('REMOVE_VIOLATION', index)
    },
    listView({ commit }, val) {
        commit('LIST_VIEW', val)
    },
    highlightedListView({ commit }, val) {
        commit('HIGHLIGHTED_LIST_VIEW', val)
    },
    restaurantView({ commit }, val) {
        commit('RESTAURANT_VIEW', val)
    },
    brandView({ commit }, val) {
        commit('BRAND_VIEW', val)
    },
    checkoutView({ commit }, val) {
        commit('CHECKOUT_VIEW', val)
    },
    listFiltered({ commit }, val) {
        commit('LIST_FILTERED', val)
    },
}

const getters = {
    initialized: (state) => state.initialized,
    config: (state) => state.config,
    restaurantFilters: (state) => state.config?.restaurant?.filter,
    currency: (state) => {
        return {
            code: state.config?.currency_code,
            locale: state.config?.currency_locale,
        }
    },
    navScroll: (state) => state.navScroll,
    cartOpen: (state) => state.cartOpen,
    violations: (state) => state.violations,
    listView: (state) => state.listView,
    highlightedListView: (state) => state.highlightedListView,
    restaurantView: (state) => state.restaurantView,
    brandView: (state) => state.brandView,
    checkoutView: (state) => state.checkoutView,
    needToSetQuery: (getters) => getters.listView || getters.restaurantView || getters.brandView,
    listFiltered: (state) => state.listFiltered,
    siteLocation: () => process.env.VUE_APP_LOCATION,
    requestInvoiceAvailable: (state) => !!state.config?.ask_billing_info_at_checkout,
    phoneFormat: (state) => state.config.country?.toUpperCase(), // https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2
    // tasks#202: IANA zone of the application's offset-less (binlog) datetimes; null until the config is loaded
    timezone: (state) => state.config?.timezone || null,
    appDebug: () => [true, 'true'].includes(process.env.VUE_APP_DEBUG),
}

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
}
